import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

export type BannerTone = 'info' | 'warning' | 'error' | 'success';

const TONE: Record<
  BannerTone,
  { wrap: string; icon: typeof Info; iconClass: string }
> = {
  info: { wrap: 'bg-ds-teal-tint border-ds-teal-line', icon: Info, iconClass: 'text-ds-teal' },
  warning: {
    wrap: 'bg-ds-amber-tint border-ds-amber-line',
    icon: AlertTriangle,
    iconClass: 'text-ds-amber-ink',
  },
  error: { wrap: 'bg-ds-red-tint border-ds-red-line', icon: AlertCircle, iconClass: 'text-ds-red-ink' },
  success: {
    wrap: 'bg-ds-green-tint border-ds-green-line',
    icon: CheckCircle2,
    iconClass: 'text-ds-green-ink',
  },
};

export function Banner({
  tone,
  title,
  children,
  action,
  className,
}: {
  tone: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const spec = TONE[tone];
  const Icon = spec.icon;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex gap-s3 rounded-pop border p-s4', spec.wrap, className)}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', spec.iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className={cn(TYPE.section, 'text-ds-text')}>{title}</p> : null}
        {children ? <p className={cn(TYPE.body, 'text-ds-text', title && 'mt-s1')}>{children}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
